(() => {
  const MOBILE_MAX = 749;
  const mq = window.matchMedia(`(max-width: ${MOBILE_MAX}px)`);
  const EDGE_PAD = 12;
  const GAP = 14;

  const isMobile = () => mq.matches;

  const positionSpots = (root) => {
    root.querySelectorAll('[data-spot]').forEach((btn) => {
      const spot = btn.closest('.maphot__spot');
      if (!spot) return;

      const x = isMobile() ? btn.dataset.mx : btn.dataset.x;
      const y = isMobile() ? btn.dataset.my : btn.dataset.y;

      spot.style.left = `${Number(x || 50)}%`;
      spot.style.top = `${Number(y || 50)}%`;
    });
  };

  const resetCardPosition = (card) => {
    if (!card) return;
    card.classList.remove('is-below');
    card.style.removeProperty('--mh-shift-x');
    card.style.removeProperty('--mh-shift-y');
    card.style.removeProperty('visibility');
  };

  const closeAllDesktop = (root) => {
    root.querySelectorAll('.maphot__spot').forEach((s) => s.classList.remove('is-open'));

    root.querySelectorAll('[data-card]').forEach((card) => {
      resetCardPosition(card);
      card.hidden = true;
      const btn = root.querySelector(`[aria-controls="${card.id}"]`);
      if (btn) btn.setAttribute('aria-expanded', 'false');
    });
  };

  const setActive = (root, id) => {
    root.querySelectorAll('[data-spot]').forEach((b) => {
      b.classList.toggle('is-active', b.dataset.id === id);
    });
    root.querySelectorAll('[data-slide]').forEach((s) => {
      s.classList.toggle('is-active', s.dataset.id === id);
    });
  };

  const scrollToSlide = (root, id) => {
    const slider = root.querySelector('[data-slider]');
    if (!slider) return;

    const slide = slider.querySelector(`[data-slide][data-id="${CSS.escape(id)}"]`);
    if (!slide) return;

    slide.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    setActive(root, id);
  };

  const clampCardIntoStage = (root, btn, card) => {
    const stage = root.querySelector('.maphot__stage');
    if (!stage || !btn || !card) return;

    resetCardPosition(card);

    card.hidden = false;
    card.style.visibility = 'hidden';

    const stageRect = stage.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();

    const cardRect0 = card.getBoundingClientRect();
    const need = cardRect0.height + GAP;
    const spaceAbove = btnRect.top - stageRect.top;
    const spaceBelow = stageRect.bottom - btnRect.bottom;

    const placeBelow = spaceBelow >= need || spaceBelow > spaceAbove;
    if (placeBelow) card.classList.add('is-below');

    requestAnimationFrame(() => {
      const s = stage.getBoundingClientRect();
      const r = card.getBoundingClientRect();

      let shiftX = 0;
      const overflowL = s.left + EDGE_PAD - r.left;
      const overflowR = r.right - (s.right - EDGE_PAD);
      if (overflowL > 0) shiftX += overflowL;
      if (overflowR > 0) shiftX -= overflowR;

      let shiftY = 0;
      const overflowT = s.top + EDGE_PAD - r.top;
      const overflowB = r.bottom - (s.bottom - EDGE_PAD);
      if (overflowT > 0) shiftY += overflowT;
      if (overflowB > 0) shiftY -= overflowB;

      card.style.setProperty('--mh-shift-x', `${shiftX}px`);
      card.style.setProperty('--mh-shift-y', `${shiftY}px`);
      card.style.removeProperty('visibility');
    });
  };

  const initMobileObserver = (root) => {
    const slider = root.querySelector('[data-slider]');
    if (!slider) return;

    const slides = Array.from(slider.querySelectorAll('[data-slide]'));
    if (!slides.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        let best = null;

        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          if (!best || e.intersectionRatio > best.intersectionRatio) best = e;
        });

        if (best && best.target && best.target.dataset.id) {
          setActive(root, best.target.dataset.id);
        }
      },
      { root: slider, threshold: [0.55, 0.7, 0.85] }
    );

    slides.forEach((s) => io.observe(s));
  };

  const getOpenDesktop = (root) => {
    const btn = root.querySelector('[data-spot][aria-expanded="true"]');
    if (!btn) return null;

    const cardId = btn.getAttribute('aria-controls');
    const card = cardId ? root.querySelector(`#${CSS.escape(cardId)}`) : null;
    if (!card || card.hidden) return null;

    return { btn, card };
  };

  /* =========================
     Mobile slider arrows
     ========================= */

  const ensureMobileNav = (root) => {
    const wrap = root.querySelector('.maphot__sliderWrap');
    const slider = root.querySelector('[data-slider]');
    if (!wrap || !slider) return null;

    const existingPrev = wrap.querySelector('.maphot__nav--prev');
    const existingNext = wrap.querySelector('.maphot__nav--next');
    if (existingPrev && existingNext) {
      return { wrap, slider, prevBtn: existingPrev, nextBtn: existingNext };
    }

    const mkBtn = (cls, label, dir) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `maphot__nav ${cls}`;
      b.setAttribute('aria-label', label);
      b.dataset.dir = String(dir);
      b.hidden = true;

      b.innerHTML =
        '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
        '<path d="M14.5 5.5L8 12l6.5 6.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>' +
        '</svg>';

      if (dir > 0) b.querySelector('path').setAttribute('d', 'M9.5 5.5L16 12l-6.5 6.5');
      return b;
    };

    const prevBtn = mkBtn('maphot__nav--prev', 'Previous', -1);
    const nextBtn = mkBtn('maphot__nav--next', 'Next', 1);

    wrap.appendChild(prevBtn);
    wrap.appendChild(nextBtn);

    return { wrap, slider, prevBtn, nextBtn };
  };

  const initMobileNavBehavior = (root) => {
    if (root.dataset.maphotNavInit === '1') return;
    root.dataset.maphotNavInit = '1';

    const nav = ensureMobileNav(root);
    if (!nav) return;

    const { slider, prevBtn, nextBtn } = nav;

    const getScrollAmount = () => {
      const first = slider.querySelector('[data-slide]');
      if (first) {
        const styles = window.getComputedStyle(slider);
        const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return first.getBoundingClientRect().width + gap;
      }
      return Math.max(240, Math.floor(slider.clientWidth * 0.85));
    };

    const getScrollerPadding = () => {
      const styles = window.getComputedStyle(slider);
      const pl = parseFloat(styles.paddingLeft || '0') || 0;
      const pr = parseFloat(styles.paddingRight || '0') || 0;
      return { pl, pr };
    };

    const setBothHidden = () => {
      prevBtn.hidden = true;
      nextBtn.hidden = true;
      prevBtn.disabled = true;
      nextBtn.disabled = true;
    };

    const updateButtons = () => {
      if (!isMobile()) {
        setBothHidden();
        return;
      }

      const maxScroll = slider.scrollWidth - slider.clientWidth;
      if (maxScroll <= 2) {
        setBothHidden();
        return;
      }

      const pad = getScrollerPadding();
      const current = slider.scrollLeft;

      const startThreshold = pad.pl + 2;
      const endThreshold = maxScroll - pad.pr - 2;

      const atStart = current <= startThreshold;
      const atEnd = current >= endThreshold;

      prevBtn.disabled = atStart;
      nextBtn.disabled = atEnd;

      prevBtn.hidden = atStart;
      nextBtn.hidden = atEnd;
    };

    const scrollByDir = (dir) => {
      slider.scrollBy({ left: getScrollAmount() * dir, behavior: 'smooth' });
    };

    prevBtn.addEventListener('click', () => scrollByDir(-1));
    nextBtn.addEventListener('click', () => scrollByDir(1));

    slider.addEventListener('scroll', updateButtons, { passive: true });
    window.addEventListener('resize', updateButtons, { passive: true });

    window.setTimeout(updateButtons, 50);
    window.setTimeout(updateButtons, 300);

    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => updateButtons());
      ro.observe(slider);
    }

    window.addEventListener('load', () => updateButtons(), { once: true });
  };

  const init = (root) => {
    positionSpots(root);

    const firstSpot = root.querySelector('[data-spot]');
    if (firstSpot && firstSpot.dataset.id) setActive(root, firstSpot.dataset.id);

    initMobileObserver(root);
    initMobileNavBehavior(root);

    const onResize = () => {
      positionSpots(root);

      if (isMobile()) {
        closeAllDesktop(root);
        return;
      }

      const open = getOpenDesktop(root);
      if (open) clampCardIntoStage(root, open.btn, open.card);
    };

    window.addEventListener('resize', onResize, { passive: true });

    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-spot]');
      const closeBtn = e.target.closest('[data-close]');

      if (closeBtn) {
        const card = closeBtn.closest('[data-card]');
        if (card) {
          const spot = card.closest('.maphot__spot');
          if (spot) spot.classList.remove('is-open');

          resetCardPosition(card);
          card.hidden = true;

          const cbtn = root.querySelector(`[aria-controls="${card.id}"]`);
          if (cbtn) cbtn.setAttribute('aria-expanded', 'false');
        }
        return;
      }

      if (btn) {
        const id = btn.dataset.id;
        if (!id) return;

        if (isMobile()) {
          scrollToSlide(root, id);
          return;
        }

        const cardId = btn.getAttribute('aria-controls');
        const card = cardId ? root.querySelector(`#${CSS.escape(cardId)}`) : null;
        if (!card) return;

        const isOpen = btn.getAttribute('aria-expanded') === 'true';
        closeAllDesktop(root);

        if (!isOpen) {
          const spot = btn.closest('.maphot__spot');
          if (spot) spot.classList.add('is-open');

          card.hidden = false;
          btn.setAttribute('aria-expanded', 'true');
          setActive(root, id);

          clampCardIntoStage(root, btn, card);

          card.querySelectorAll('img').forEach((img) => {
            if (img.complete) return;
            img.addEventListener(
              'load',
              () => {
                const open = getOpenDesktop(root);
                if (open && open.btn === btn && open.card === card) {
                  clampCardIntoStage(root, btn, card);
                }
              },
              { once: true }
            );
          });
        }
        return;
      }

      if (isMobile()) return;

      const clickedInsideCard = e.target.closest('[data-card]');
      if (!clickedInsideCard) closeAllDesktop(root);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      closeAllDesktop(root);
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-maphot]').forEach(init);
  });

  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll('[data-maphot]').forEach(init);
  });
})();